import { useState, useEffect, useCallback } from "react";

const KEY = "amwap_supply_cart";
const EVT = "amwap-cart-changed";

const read = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || [];
  } catch {
    return [];
  }
};

const write = (items) => {
  localStorage.setItem(KEY, JSON.stringify(items));
  window.dispatchEvent(new Event(EVT));
};

export const openCartDrawer = () => window.dispatchEvent(new Event("amwap-open-cart"));

// Max orderable qty for an item — capped to stock only when the admin enabled the limit.
export const maxFor = (item) =>
  item?.enforce_stock && item?.stock_qty != null ? Math.max(0, Number(item.stock_qty)) : Infinity;

export const unitPrice = (item) => {
  let best = item.price || 0;
  for (const t of [...(item.tiered_pricing || [])].sort((a, b) => a.min_qty - b.min_qty)) {
    if (item.quantity >= t.min_qty && t.price != null) best = t.price;
  }
  return best;
};

export const useCart = () => {
  const [items, setItems] = useState(read);

  useEffect(() => {
    const sync = () => setItems(read());
    window.addEventListener(EVT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const addItem = useCallback((product, qty = 1) => {
    const cur = read();
    const idx = cur.findIndex((i) => i.product_id === product.id);
    const cap = product.enforce_stock && product.stock_qty != null ? Math.max(0, Number(product.stock_qty)) : Infinity;
    if (idx >= 0) {
      cur[idx].enforce_stock = product.enforce_stock;
      cur[idx].stock_qty = product.stock_qty;
      cur[idx].quantity = Math.min(cur[idx].quantity + qty, cap);
    } else {
      cur.push({
        product_id: product.id,
        name: product.name,
        image: product.images?.[0] || "",
        price: product.price,
        unit: product.unit,
        tiered_pricing: product.tiered_pricing || [],
        enforce_stock: product.enforce_stock,
        stock_qty: product.stock_qty,
        quantity: Math.min(qty, cap),
      });
    }
    write(cur);
  }, []);

  const setQty = useCallback((productId, qty) => {
    const cur = read().map((i) => (i.product_id === productId ? { ...i, quantity: Math.min(Math.max(1, qty), maxFor(i)) } : i));
    write(cur);
  }, []);

  const removeItem = useCallback((productId) => {
    write(read().filter((i) => i.product_id !== productId));
  }, []);

  const clear = useCallback(() => write([]), []);

  const count = items.reduce((n, i) => n + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + unitPrice(i) * i.quantity, 0);

  return { items, addItem, setQty, removeItem, clear, count, subtotal };
};
